import * as S from './styles';
import { useCart, useTables } from '@/hooks';
import Modal from '../modal/Modal';
import { colors } from '@/constants';
import { RiInformationLine } from 'react-icons/ri';
import { buildImageSrc, buildImageSrcError } from '@/shared';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { ProductProps, Attributes, PackagesTypesProps, PriceProductProps } from '@/types';
import * as Packing from '@/components/packaging/styles';
import { packingTypes } from '@/shared';

interface ModalCustomAttributesProps {
  isOpen: boolean;
  product: ProductProps | null;
  packQuantity: number;
  pack: PackagesTypesProps | null;
  openModal: Dispatch<SetStateAction<boolean>>;
  price: PriceProductProps | null;
}

const attributesLabels = {
  ean_grp: 'EAN',
  dun_grp: 'DUN',
  peso_grp: 'Peso',
  grp_marca: 'Marca',
  familia_grp: 'Família',
  dimensao_grp: 'Dimensão',
  fabricante_401: 'Fabricante',
  volume_104: 'Volume',
  palavra_chave_grp: 'Palavra-chave',
  description: 'Especificação detalhada',
  cod_promocao: 'Cód. Promoção',
  formato_101: 'Formato',
  fragrância_201: 'Fragrância',
  tipo_81: 'Tipo',
  embalagem_105: 'Embalagem',
  MD_ALTURA: 'Altura',
  MD_LARGURA: 'Largura',
  MD_PROFUNDIDADE: 'Profundidade',
  MD_CUBAGEM: 'Cubagem',
  QTD_CX_CAMADA: 'Qtd. Caixa/Camada',
  QTD_CX_PALETE: 'Qtd. Caixa/Palete',
  MD_PESO_LIQUIDO: 'Peso Líquido',
  MD_PESO_BRUTO: 'Peso Bruto',
  
};

const ModalCustomAttributes = ({ product, pack, packQuantity, openModal, isOpen = true, price }: ModalCustomAttributesProps) => {
  const { tables } = useTables();
  const { getStockMsg } = useCart();
  const [currentIsOpen, setCurrentIsOpen] = useState(false);
  const [selectedPack, setSelectedPack] = useState<PackagesTypesProps | null>(pack);

  useEffect(() => {
    setTimeout(() => {
      setSelectedPack(pack || null);
      setCurrentIsOpen(isOpen);
    }, 300);
  }, [isOpen]);

  const hasExpiring = useMemo(() => {
    if (!product?.COD_PRODUTO) return false;
    return getStockMsg(product?.COD_PRODUTO);
  }, [getStockMsg, product?.COD_PRODUTO]);

  const image = useMemo(() => product?.IMAGEM || tables.current.imagens?.[product?.COD_PRODUTO!], [product, tables]);

  const expiringSeverity = useMemo<'danger' | 'warning' | 'normal'>(() => {
    const msg = hasExpiring || '';
    const match = msg.match(/(\d+)/);
    const days = match ? Number(match[1]) : NaN;
    if (!isNaN(days)) {
      if (days <= 90) return 'danger';
      if (days <= 120) return 'warning';
    }
    return 'normal';
  }, [hasExpiring]);

  const dun = useMemo(() => {
    return product?.EMBALAGEM?.find((i) => i.SG_EMBALAGEM === pack && i.QTD_EMBALAGEM * i.QTD_MULTIPLO_VENDA === packQuantity)
      ?.DUN_PRODUTO;
  }, [product, pack, packQuantity]);

  let ean = { ean_grp: '' };

  const atributos = useMemo(() => {
    if (!product?.ATRIBUTOS) return [];

    const list = product?.ATRIBUTOS?.filter((i) => {
      const key = Object.keys(i)?.[0] as Attributes;
      const isValid = i?.[key] !== null;
      if (key === 'ean_grp' && isValid) ean = { ean_grp: i?.[key]! };
      else if (key === 'ean_grp') {
        ean = { ean_grp: String(product.EMBALAGEM?.find(emb => emb?.EAN_PRODUTO)?.EAN_PRODUTO ?? '') };
      }
      return isValid && key !== 'ean_grp';
    });

    if (!!price?.COD_PROMOCAO) list.unshift({ cod_promocao: `${price.COD_PROMOCAO}` });
    if (dun && dun.toString() !== '0') list.unshift({ dun_grp: dun.toString() });
    list.unshift(ean);

    return list;
  }, [product, dun]);

  const orderedPackaging = useMemo(() => {
    const list = [...(product?.EMBALAGEM || [])]?.sort((a, b) => a?.QTD_EMBALAGEM * a?.QTD_MULTIPLO_VENDA - b?.QTD_EMBALAGEM * b?.QTD_MULTIPLO_VENDA).reverse();
    return list;
  }, [product]);


  const selectedPackagingAttributes = useMemo(() => {
    const selectedPackaging = orderedPackaging.find((emb) => emb?.SG_EMBALAGEM === selectedPack);

    return [
      { MD_ALTURA: selectedPackaging?.MD_ALTURA },
      { MD_LARGURA: selectedPackaging?.MD_LARGURA },
      { MD_PROFUNDIDADE: selectedPackaging?.MD_PROFUNDIDADE },
      { MD_CUBAGEM: selectedPackaging?.MD_CUBAGEM },
      { QTD_CX_CAMADA: selectedPackaging?.QTD_CX_CAMADA },
      { QTD_CX_PALETE: selectedPackaging?.QTD_CX_PALETE },
      { MD_PESO_LIQUIDO: selectedPackaging?.MD_PESO_LIQUIDO },
      { MD_PESO_BRUTO: selectedPackaging?.MD_PESO_BRUTO },
    ]
  }, [orderedPackaging, selectedPack]);

  return (
    <Modal
      id={`modal-custom-attributes-${product?.COD_PRODUTO ?? ''}`}
      direction="right"
      isOpen={currentIsOpen}
      isScrollable={false}
      closeModal={openModal}
    >
      {currentIsOpen && product && pack && price && (
        <>
          <S.Title>Detalhes do produto</S.Title>
          <S.Divider />
          <S.ModalContent>
            <S.ProductIdGrid>
              <S.ProductImageWrapper $hasExpiring={!!hasExpiring}>
                <S.ProductImage alt={product.DESC_PRODUTO} src={buildImageSrc(image)} onError={buildImageSrcError} />
              </S.ProductImageWrapper>
              <S.ProductName $hasExpiring={!!hasExpiring} $severity={expiringSeverity}>
                {product.DESC_PRODUTO_BUSCA || product.DESC_PRODUTO}
              </S.ProductName>
            </S.ProductIdGrid>

            {hasExpiring && (
              <S.ExpiringView>
                <RiInformationLine size={18} color={expiringSeverity === 'danger' ? colors.alert.red200 : colors.alert.orange300} />

                <S.ExpiringText $severity={expiringSeverity}>{hasExpiring}</S.ExpiringText>
              </S.ExpiringView>
            )}

            <S.ProductTable>
              <S.TableBody>
                {atributos?.map((item, index) => {
                  const key = Object.keys(item)?.[0] as Attributes;
                  const value = item[key];
                  if (key && value) {
                    if (!!attributesLabels[key as keyof typeof attributesLabels]) {
                      return (
                        <S.TableRow key={`${key}-${index}`} $applyBackground={(index + 1) % 2 !== 0}>
                          <S.TableCell $bold={'true'}>{attributesLabels[key as keyof typeof attributesLabels]}</S.TableCell>
                          <S.TableCell dangerouslySetInnerHTML={{ __html: value }} />
                        </S.TableRow>
                      );
                    } else {
                      return (
                        <S.TableRow key={`${key}-${index}`} $applyBackground={(index + 1) % 2 !== 0}>
                          <S.TableCell $bold={'true'} $capitalize={true}>
                            {key?.split('_')?.[0]}
                          </S.TableCell>
                          <S.TableCell>{value}</S.TableCell>
                        </S.TableRow>
                      );
                    }
                  }
                  return null;
                })}
              </S.TableBody>
            </S.ProductTable>

            <S.PackagingRow>
              {orderedPackaging?.map((embalagem) => {
                const isActive = selectedPack === embalagem?.SG_EMBALAGEM;
                const packImage = packingTypes[embalagem?.SG_EMBALAGEM as PackagesTypesProps]?.icon;
                return (
                  <Packing.BoxEmbalagem
                    size={50}
                    $fullBorder={isActive}
                    $isactive={isActive}
                    $unityPack={orderedPackaging.length === 1}
                    onClick={() => {
                      setSelectedPack(embalagem?.SG_EMBALAGEM);
                    }}
                    key={`pack-${product.COD_PRODUTO}-${embalagem?.SG_EMBALAGEM}-${embalagem?.QTD_EMBALAGEM}`}
                  >
                    {packImage && <Packing.PackImage src={packImage} alt={embalagem?.SG_EMBALAGEM ?? ''} />}
                    {isActive && <Packing.CheckFill size={24} color={colors.alert.green110} />}

                    <Packing.BoxLabel>
                      <Packing.BoxQtdEmbalagem>
                        <Packing.QtdEmbalagem>{embalagem?.QTD_EMBALAGEM * embalagem?.QTD_MULTIPLO_VENDA}/un</Packing.QtdEmbalagem>
                      </Packing.BoxQtdEmbalagem>
                    </Packing.BoxLabel>
                  </Packing.BoxEmbalagem>
                )
              })}
            </S.PackagingRow>

            <S.TableBody>
              {selectedPackagingAttributes?.map((item, index) => {
                const [key, value] = Object.entries(item)[0];
                
                return (
                  <S.TableRow key={`${key}-${index}`} $applyBackground={(index + 1) % 2 !== 0}>
                    <S.TableCell $bold={'true'}>{attributesLabels[key as keyof typeof attributesLabels]}</S.TableCell>
                    <S.TableCell>{value?.toString()}</S.TableCell>
                  </S.TableRow>
                );
              })}
            </S.TableBody>
          </S.ModalContent>
        </>
      )}
    </Modal>
  );
};

export default ModalCustomAttributes;
